import EditorJS from '@editorjs/editorjs';
// eslint-disable-next-line import/no-unresolved
import { Events } from '@editorjs/editorjs/types/api';

import {
  InstructionEventCallbackData,
  InstructionEvent,
  InstructionAttribute,
  InstructionCSSClasses,
} from './types';
import { Article } from '../../../types/items';
import { parseJsonToEditorStateWithInstruction } from '../../../services/editorJS/utils';
import { ImageInstruction, VideoInstruction } from '../../../types/instructions';
import { Block } from '../../../services/editorJS/types';

export const emitInstructionEvent = <T extends InstructionEvent>(
  editorEvents: Events,
  event: T,
  data: InstructionEventCallbackData[T],
): void => {
  editorEvents.emit(event, data);
};

export const addInstructionEvent = <T extends InstructionEvent>(
  editorEvents: Events,
  event: T,
  callback: (data: InstructionEventCallbackData[T]) => void,
): void => {
  editorEvents.on(event, callback);
};

export const addInstructionEventByEditor = <T extends InstructionEvent>(
  editor: EditorJS,
  event: T,
  callback: (data: InstructionEventCallbackData[T]) => void,
): void => {
  editor.isReady.then(() => {
    editor.on(event, callback);
  });
};

export const emitInstructionEventByEditor = <T extends InstructionEvent>(
  editor: EditorJS,
  event: T,
  data: InstructionEventCallbackData[T],
): void => {
  editor.isReady.then(() => {
    editor.emit(event, data);
  });
};

export const findInstructionElement = (
  {
    instructionId, isInline = true, htmlElement, tooltip,
  }: { instructionId: string; isInline?: boolean; htmlElement?: HTMLElement; tooltip?: string },
) => {
  if (window.scriptEditorRef) {
    const selector = isInline ? InstructionCSSClasses.INLINE_INSTRUCTION_WRAPPER
      : InstructionCSSClasses.BLOCK_INSTRUCTION_WRAPPER;
    if (htmlElement && tooltip) {
      const actionElement = htmlElement.querySelector(
        `.${selector}[${InstructionAttribute.instructionId}="${instructionId}"]`,
      );
      const actionByScriptRef = window.scriptEditorRef.querySelector(
        `.${selector}[${InstructionAttribute.instructionId}="${instructionId}"]`,
      );
      if (actionElement && actionByScriptRef) {
        const firstChildActionElement = actionElement.firstElementChild;
        const firstChildActionByScriptRef = actionByScriptRef.firstElementChild;
        if (firstChildActionElement && firstChildActionByScriptRef) {
          firstChildActionElement.setAttribute(InstructionAttribute.tooltip, tooltip);
          firstChildActionByScriptRef.setAttribute(InstructionAttribute.tooltip, tooltip);
        }
      }
      return actionElement;
    }
    return window.scriptEditorRef.querySelector(
      `.${selector}[${InstructionAttribute.instructionId}="${instructionId}"]`,
    );
  }
  return null;
};

export const setInstructionActionTooltip = async (
  {
    instructionId, tooltip, isInline = true, scriptBlocks,
  }: { instructionId: string; tooltip: string; isInline?: boolean; scriptBlocks?: Block[] },
) => {
  if (scriptBlocks) {
    scriptBlocks.forEach((block) => {
      if (block.type === 'paragraph') {
        if (block.data.text?.includes(instructionId)) {
          const htmlElement = document.createElement('div');
          htmlElement.innerHTML = block.data.text;
          const instructionElement = findInstructionElement({ instructionId, htmlElement, tooltip });
          if (instructionElement) {
            const actionElement = instructionElement.querySelector(`.${InstructionCSSClasses.INLINE_INSTRUCTION_ACTION}`);
            if (actionElement) {
              block.data.text = htmlElement.innerHTML;
            }
          }
        }
      }
    });
  } else {
    const instructionElement = findInstructionElement({ instructionId, isInline });
    if (instructionElement) {
      const actionElement = instructionElement.querySelector(`.${InstructionCSSClasses.INLINE_INSTRUCTION_ACTION}`);
      if (actionElement) {
        actionElement.setAttribute(InstructionAttribute.tooltip, tooltip);
      }
    }
  }
};

export const updateAllInstructionActionEvents = async (
  instanceEditor: EditorJS,
  refEditor: HTMLDivElement,
) => {
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_IMAGE_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );

  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_AUDIO_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_TEXT_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_NOTE_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_VIDEO_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_TITLE_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_NAME_SCRIPT_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_DOCUMENT_PAGE_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
  emitInstructionEventByEditor(
    instanceEditor,
    InstructionEvent.UPDATE_WEB_PAGE_ACTION_EVENT_LISTENERS,
    {
      instanceEditor,
      refEditor,
    },
  );
};

export const removeInstructionElement = async (
  { instructionId, isInline = true }: { instructionId: string; isInline?: boolean },
) => {
  if (isInline) {
    const selector = isInline ? InstructionCSSClasses.INLINE_INSTRUCTION_CONTENT
      : InstructionCSSClasses.BLOCK_INSTRUCTION_CONTENT;

    const range = new Range();
    const element = findInstructionElement({ instructionId, isInline });
    if (element) {
      const contentElement = element.querySelector(`.${selector}`);
      if (contentElement) {
        const fragment = document.createDocumentFragment();
        contentElement.childNodes.forEach((item) => {
          fragment.appendChild(item);
        });
        range.selectNode(element);
        range.deleteContents();
        range.insertNode(fragment);
      }
    }
  }
};

export const filterProcessedFiles = (articles: Article[]) => articles.filter((item) => !item.ref.document?.ref.is_processing);

export const sortDocumentsByTitle = (articles:Article[]) => articles.sort((a, b) => {
  if (a.ref.video) {
    if (a.ref.video!.title.toLowerCase() > b.ref.video!.title.toLowerCase()) {
      return 1;
    }
    if (a.ref.video!.title.toLowerCase() < b.ref.video!.title.toLowerCase()) {
      return -1;
    }
  } else {
    if (a.ref.document!.title.toLowerCase() > b.ref.document!.title.toLowerCase()) {
      return 1;
    }
    if (a.ref.document!.title.toLowerCase() < b.ref.document!.title.toLowerCase()) {
      return -1;
    }
  }
  return 0;
});

export const renderExclamationIcon = (action: HTMLElement, instructionId: string) => {
  const timer = setTimeout(() => {
    const scriptInput: HTMLInputElement | null = document.querySelector('input[name="script"]');
    if (scriptInput) {
      const { instructions } = parseJsonToEditorStateWithInstruction(scriptInput.value);
      const faExclamationCircleIcon = action.querySelector('.exclamationIcon');
      const foundInstruction = instructions.find((instruction) => instruction.id === instructionId);
      if (foundInstruction && ((foundInstruction as ImageInstruction).data?.documentId === -2
          || (foundInstruction as VideoInstruction).data?.videoId === -2)) {
        if (!faExclamationCircleIcon) {
          const exclamationCircleIcon = document.createElement('div');
          exclamationCircleIcon!.classList.add('exclamationIcon');
          action.append(exclamationCircleIcon!);
        }
      } else if (faExclamationCircleIcon) {
        action.removeChild(faExclamationCircleIcon);
      }
    }
    clearTimeout(timer);
  }, 0);
};

export const getInstructionFromSpan = (span: HTMLSpanElement): string|null => {
  const { parentElement } = span;
  if (parentElement) {
    const instructionId = parentElement.getAttribute(InstructionAttribute.instructionId);
    if (instructionId) {
      return instructionId;
    }
  }
  return null;
};

export const removeInlineInstructionWrapper = (wrapper: HTMLSpanElement) => {
  const callback = (mutationList: MutationRecord[]) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        wrapper.remove();
      }
    });
  };
  const observer = new MutationObserver(callback);
  observer.observe(wrapper, { childList: true });
};
